import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
  QrCode,
  MessageCircle,
  Mail,
  Phone,
  Eye,
  EyeOff,
  Loader2,
  User,
  LogIn,
} from 'lucide-react';
import QRCode from '../components/QRCode';
import ThemeToggle from '../components/ThemeToggle';
import authService from '../services/authService';
import { getSigninUrl, isCasdoorEnabled, isSilentSigninRequired } from '../config/casdoor';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loginMode, setLoginMode] = useState('email');
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [loginStatus, setLoginStatus] = useState('idle');
  const [error, setError] = useState('');
  const [pollingInterval, setPollingInterval] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(300);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [username, setUsername] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(60);
  const timeLeftRef = useRef(timeLeft);
  const isGeneratingRef = useRef(false);

  const startPolling = useCallback((qrCodeId) => {
    const interval = setInterval(async () => {
      try {
        const statusData = await authService.checkLoginStatus(qrCodeId);
        if (statusData.status === 'confirmed') {
          clearInterval(interval);
          setPollingInterval(null);
          setLoginStatus('success');
          authService.storeUserData(statusData.user_info, statusData.token);
          setTimeout(() => navigate('/dashboard'), 1000);
        } else if (statusData.status === 'expired') {
          clearInterval(interval);
          setPollingInterval(null);
          setLoginStatus('error');
          setError('二维码已过期，请重新生成');
        }
      } catch (err) {
        console.error('Error checking login status:', err);
      }
    }, 2000);
    setPollingInterval(interval);
  }, [navigate]);

  const generateQRCode = useCallback(async () => {
    if (isGeneratingRef.current) return;
    try {
      isGeneratingRef.current = true;
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
      setLoginStatus('loading');
      setError('');
      const data = await authService.generateQRCode();
      setQrCodeData(data);
      setExpiresAt(new Date(data.expires_at));
      setTimeLeft(3000);
      timeLeftRef.current = 3000;
      setLoginStatus('scanning');
      startPolling(data.qr_code_id);
    } catch (err) {
      setLoginStatus('error');
      setError(err.response?.data?.error || '生成二维码失败');
    } finally {
      setTimeout(() => { isGeneratingRef.current = false; }, 1000);
    }
  }, [startPolling, pollingInterval]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoginStatus('loading');
    setError('');
    try {
      if (isRegisterMode) {
        if (!email || !username || !password || !confirmPassword) {
          setError('所有字段都是必填的');
          setLoginStatus('error');
          return;
        }
        if (password !== confirmPassword) {
          setError('密码不匹配');
          setLoginStatus('error');
          return;
        }
        await authService.register(email, username, password, confirmPassword);
        setLoginStatus('success');
        setRegistrationSuccess(true);
        setTimeout(() => {
          setRegistrationSuccess(false);
          handleModeChange(false);
        }, 2000);
      } else {
        if (!email || !password) {
          setError('邮箱/用户名和密码是必填的');
          setLoginStatus('error');
          return;
        }
        const response = await authService.loginWithPassword(email, password);
        authService.storeUserData(response.user, response.access_token);
        setLoginStatus('success');
        setTimeout(() => navigate('/dashboard'), 1000);
      }
    } catch (err) {
      console.error('Login error:', err);
      setLoginStatus('error');
      const errorMessage = err.response?.data?.error || err.response?.data?.message || (isRegisterMode ? '注册失败' : '邮箱/用户名或密码无效');
      setError(errorMessage);
      setTimeout(() => {
        if (error === errorMessage) {
          setError('');
          setLoginStatus('idle');
        }
      }, 10000);
    }
  };

  const handleSendOtp = async () => {
    if (!phone) {
      setError('手机号码是必填的');
      setLoginStatus('error');
      return;
    }
    setLoginStatus('loading');
    setError('');
    try {
      await authService.sendPhoneOtp(phone);
      setOtpSent(true);
      setLoginStatus('idle');
      setOtpCountdown(60);
    } catch (err) {
      setLoginStatus('error');
      setError(err.response?.data?.error || err.response?.data?.message || '发送验证码失败');
    }
  };

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    if (!phone || !otp) {
      setError('手机号码和验证码是必填的');
      setLoginStatus('error');
      return;
    }
    setLoginStatus('loading');
    setError('');
    try {
      const response = await authService.loginWithPhoneOtp(phone, otp);
      authService.storeUserData(response.user, response.access_token);
      setLoginStatus('success');
      setTimeout(() => navigate('/dashboard'), 1000);
    } catch (err) {
      setLoginStatus('error');
      setError(err.response?.data?.error || err.response?.data?.message || '验证码验证失败');
    }
  };

  const handleSocialLogin = (provider) => {
    setLoginStatus('error');
    setError(`${provider} 登录尚未实现`);
  };

  // Handle SSO login redirect
  const handleSsoLogin = useCallback(() => {
    setSsoLoading(true);
    setError('');

    // Generate a random state for CSRF protection
    const state = Math.random().toString(36).substring(7);
    sessionStorage.setItem('oauth_state', state);

    // Get the signin URL and redirect
    const signinUrl = getSigninUrl(state);
    if (signinUrl) {
      window.location.href = signinUrl;
    } else {
      setSsoLoading(false);
      setError('SSO 配置错误');
      setLoginStatus('error');
    }
  }, []);

  const handleModeChange = (newMode) => {
    setIsRegisterMode(newMode);
    setError('');
    setEmail('');
    setPassword('');
    setUsername('');
    setConfirmPassword('');
    setLoginStatus('idle');
    setRegistrationSuccess(false);
    setPhone('');
    setOtp('');
    setOtpSent(false);
    setOtpCountdown(60);
  };

  const clearErrorOnInput = () => {
    if (error && loginStatus === 'error') {
      setError('');
      setLoginStatus('idle');
    }
  };

  // Check for SSO configuration and silent signin
  useEffect(() => {
    // Check if SSO is enabled
    const checkSsoConfig = async () => {
      try {
        const config = await authService.getCasdoorConfig();
        setSsoEnabled(config?.enabled || false);
      } catch (err) {
        // SSO not configured, use legacy login
        setSsoEnabled(false);
      }
    };

    // Also check from frontend config
    if (isCasdoorEnabled()) {
      setSsoEnabled(true);
    } else {
      checkSsoConfig();
    }

    // Handle silent signin for SSO (coming from another app)
    if (isSilentSigninRequired()) {
      handleSsoLogin();
    }

    // Check for error in URL params
    const urlError = searchParams.get('error');
    if (urlError) {
      setError(urlError === 'sso_failed' ? 'SSO 登录失败' : urlError);
      setLoginStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (loginMode === 'qr' && !isGeneratingRef.current) {
      generateQRCode();
    }
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    };
  }, [loginMode, generateQRCode, pollingInterval]);

  useEffect(() => {
    if (expiresAt && loginStatus === 'scanning') {
      const timer = setInterval(() => {
        timeLeftRef.current -= 1;
        setTimeLeft(timeLeftRef.current);
        if (timeLeftRef.current <= 0) {
          clearInterval(timer);
          setLoginStatus('error');
          setError('二维码已过期，请重新生成');
          if (pollingInterval) clearInterval(pollingInterval);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [expiresAt, loginStatus, pollingInterval]);

  useEffect(() => {
    if (otpSent && otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpSent, otpCountdown]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderEmailForm = () => (
    <form onSubmit={handleEmailLogin} className="space-y-4">
      <Tabs value={isRegisterMode ? 'register' : 'login'} onValueChange={(v) => handleModeChange(v === 'register')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="login">登录</TabsTrigger>
          <TabsTrigger value="register">注册</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-2">
        <Label htmlFor="email">{isRegisterMode ? '邮箱' : '邮箱或用户名'}</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="email"
            type="text"
            placeholder={isRegisterMode ? '请输入邮箱' : '请输入邮箱或用户名'}
            value={email}
            onChange={(e) => { setEmail(e.target.value); clearErrorOnInput(); }}
            className="pl-10"
          />
        </div>
      </div>

      {isRegisterMode && (
        <div className="space-y-2">
          <Label htmlFor="username">用户名</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="username"
              type="text"
              placeholder="请输入用户名"
              value={username}
              onChange={(e) => { setUsername(e.target.value); clearErrorOnInput(); }}
              className="pl-10"
            />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="password">密码</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="请输入密码"
            value={password}
            onChange={(e) => { setPassword(e.target.value); clearErrorOnInput(); }}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {isRegisterMode && (
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">确认密码</Label>
          <Input
            id="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            placeholder="请再次输入密码"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); clearErrorOnInput(); }}
          />
        </div>
      )}

      {loginStatus === 'error' && error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {registrationSuccess && (
        <Alert className="bg-success/10 text-success border-success/30">
          <AlertDescription>注册成功！请等待管理员审核。</AlertDescription>
        </Alert>
      )}

      {loginStatus === 'success' && !isRegisterMode && (
        <Alert className="bg-success/10 text-success border-success/30">
          <AlertDescription>登录成功！正在跳转...</AlertDescription>
        </Alert>
      )}

      <Button
        type="submit"
        className="w-full"
        data-testid="login-submit-button"
        disabled={loginStatus === 'loading' || registrationSuccess}
      >
        {loginStatus === 'loading' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {loginStatus === 'loading' ? (isRegisterMode ? '创建账户中...' : '登录中...') : (isRegisterMode ? '注册' : '登录')}
      </Button>
    </form>
  );

  const renderPhoneForm = () => (
    <form onSubmit={handlePhoneLogin} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="phone">手机号码</Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="phone"
            type="tel"
            placeholder="请输入手机号码"
            value={phone}
            onChange={(e) => { setPhone(e.target.value); clearErrorOnInput(); }}
            className="pl-10"
            disabled={otpSent}
          />
        </div>
      </div>

      {otpSent && (
        <div className="space-y-2">
          <Label htmlFor="otp">验证码</Label>
          <Input
            id="otp"
            type="text"
            placeholder="请输入验证码"
            value={otp}
            onChange={(e) => { setOtp(e.target.value); clearErrorOnInput(); }}
          />
        </div>
      )}

      {loginStatus === 'error' && error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loginStatus === 'success' && (
        <Alert className="bg-success/10 text-success border-success/30">
          <AlertDescription>登录成功！正在跳转...</AlertDescription>
        </Alert>
      )}

      {!otpSent ? (
        <Button
          type="button"
          className="w-full"
          onClick={handleSendOtp}
          disabled={loginStatus === 'loading'}
        >
          {loginStatus === 'loading' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {loginStatus === 'loading' ? '发送中...' : `发送验证码${otpCountdown < 60 ? ` (${otpCountdown})` : ''}`}
        </Button>
      ) : (
        <Button type="submit" className="w-full" disabled={loginStatus === 'loading'}>
          {loginStatus === 'loading' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {loginStatus === 'loading' ? '验证中...' : '验证登录'}
        </Button>
      )}
    </form>
  );

  // eslint-disable-next-line no-unused-vars
  const renderQRForm = () => (
    <div className="space-y-4">
      <div className="flex justify-center">
        <QRCode
          qrCodeUrl={qrCodeData?.qr_code_url}
          isLoading={loginStatus === 'loading'}
          error={loginStatus === 'error' ? error : ''}
        />
      </div>

      <div className="text-center space-y-2">
        <p className="text-sm text-muted-foreground">
          {loginStatus === 'scanning' ? '请使用微信扫描二维码登录' :
           loginStatus === 'loading' ? '正在生成二维码...' :
           loginStatus === 'success' ? '登录成功！正在跳转...' :
           loginStatus === 'error' ? '登录失败' : '准备登录'}
        </p>
        {loginStatus === 'scanning' && timeLeft > 0 && (
          <p className="text-xs text-muted-foreground">有效期: {formatTime(timeLeft)}</p>
        )}
      </div>

      {loginStatus === 'error' && (
        <>
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button variant="outline" className="w-full" onClick={generateQRCode}>
            重新生成二维码
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative">
      {/* Theme Toggle - Fixed position top right */}
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">FQ Tools</CardTitle>
          <CardDescription>销售/库存管理系统</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* SSO Login Button - Primary option when enabled */}
          {ssoEnabled && (
            <>
              <Button
                className="w-full"
                size="lg"
                onClick={handleSsoLogin}
                disabled={ssoLoading}
              >
                {ssoLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <LogIn className="mr-2 h-4 w-4" />
                )}
                {ssoLoading ? '正在跳转...' : '使用 SSO 登录'}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">或使用其他方式</span>
                </div>
              </div>
            </>
          )}

          <Tabs value={loginMode} onValueChange={setLoginMode}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                邮箱
              </TabsTrigger>
              <TabsTrigger value="phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                手机
              </TabsTrigger>
            </TabsList>
            <TabsContent value="email" className="mt-4">
              {renderEmailForm()}
            </TabsContent>
            <TabsContent value="phone" className="mt-4">
              {renderPhoneForm()}
            </TabsContent>
          </Tabs>

          {!ssoEnabled && (
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">或</span>
              </div>
            </div>
          )}

          {!ssoEnabled && (
            <div className="flex justify-center gap-4">
              <Button variant="outline" size="icon" onClick={() => handleSocialLogin('qr')}>
                <QrCode className="h-5 w-5" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => handleSocialLogin('wechat')}>
                <MessageCircle className="h-5 w-5" />
              </Button>
            </div>
          )}

          <div className="text-center">
            <Button variant="link" onClick={() => navigate('/forgot-password')}>
              忘记密码？
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
